import { useMemo, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Check, ChevronDown, Loader2, Plus, X } from 'lucide-react';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '@/components/ui/popover';

const ASSIGN_ROLES = new Set(['owner', 'dm_creative', 'dm_technical', 'dm_architecture']);

/**
 * One-click project assignment for a single teammate.
 * - Owner + Design Managers can toggle assignments (matches master-projects EDIT_ROLES).
 * - Everyone else sees a read-only summary.
 * - Optimistic UI: chips update instantly, revert on API error.
 */
export function AssignProjectDropdown({
  member,                 // { id, name, email }
  projects = [],          // full for-mapping list — used to render options
  currentIds = [],        // project ids the member is already on
  role,                   // caller's role (from useAuth)
  onChanged,              // async () => void — parent refetches
}) {
  const canAssign = ASSIGN_ROLES.has(role);
  const [open, setOpen] = useState(false);
  const [busyProjectId, setBusyProjectId] = useState(null);
  const [localAssigned, setLocalAssigned] = useState(currentIds);
  // Track optimistic state so the parent's next refetch can reset us.
  useMemo(() => setLocalAssigned(currentIds), [currentIds.join(',')]); // eslint-disable-line

  const assignedSet = useMemo(() => new Set(localAssigned), [localAssigned]);
  const assignedProjects = useMemo(
    () => projects.filter(p => assignedSet.has(p.id)),
    [projects, assignedSet],
  );
  const unassignedProjects = useMemo(
    () => projects.filter(p => !assignedSet.has(p.id)),
    [projects, assignedSet],
  );

  const toggle = async (projectId, currentlyAssigned) => {
    if (!canAssign) return;
    setBusyProjectId(projectId);
    // Optimistic update.
    setLocalAssigned(prev => currentlyAssigned ? prev.filter(id => id !== projectId) : [...prev, projectId]);
    try {
      await api.post('/master-projects/assign-project-to-member', {
        project_id: projectId,
        member_id: member.id,
        assigned: !currentlyAssigned,
      });
      toast.success(currentlyAssigned ? 'Unassigned' : 'Assigned');
      if (onChanged) await onChanged();
    } catch (e) {
      // Revert on failure.
      setLocalAssigned(prev => currentlyAssigned ? [...prev, projectId] : prev.filter(id => id !== projectId));
      toast.error(parseErr(e));
    } finally { setBusyProjectId(null); }
  };

  return (
    <div className="flex items-center flex-wrap gap-1.5" data-testid={`assign-projects-${member.id}`}>
      {assignedProjects.length === 0 && (
        <span className="text-[11.5px] italic text-[hsl(var(--foreground-subtle))]">No projects</span>
      )}
      {assignedProjects.map(p => (
        <span
          key={p.id}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] bg-[hsl(var(--primary)/.10)] text-[hsl(var(--primary))]"
          data-testid={`assigned-chip-${member.id}-${p.id}`}
        >
          {p.project_name}
          {canAssign && (
            <button
              onClick={() => toggle(p.id, true)}
              disabled={busyProjectId === p.id}
              aria-label={`Remove ${p.project_name}`}
              className="hover:opacity-70"
              data-testid={`unassign-btn-${member.id}-${p.id}`}
            >
              {busyProjectId === p.id ? <Loader2 size={10} className="animate-spin" /> : <X size={10} />}
            </button>
          )}
        </span>
      ))}

      {canAssign && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border border-dashed hover:border-[hsl(var(--primary))] hover:text-[hsl(var(--primary))]"
              style={{ borderColor: 'hsl(var(--border))' }}
              data-testid={`assign-open-${member.id}`}
            >
              <Plus size={10} />Assign project<ChevronDown size={10} />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[260px] p-1 max-h-[300px] overflow-auto" align="start">
            {projects.length === 0 && (
              <div className="text-[12px] text-[hsl(var(--foreground-muted))] p-3 text-center">
                No active projects.
              </div>
            )}
            {unassignedProjects.map(p => (
              <button
                key={p.id}
                onClick={() => { toggle(p.id, false); }}
                disabled={busyProjectId === p.id}
                data-testid={`assign-option-${member.id}-${p.id}`}
                className="w-full text-left px-2.5 py-1.5 rounded text-[12.5px] hover:bg-[hsl(var(--surface-1))] flex items-center gap-2"
              >
                <div className="w-4 h-4 rounded border grid place-items-center flex-none"
                     style={{ borderColor: 'hsl(var(--border))' }}>
                  {busyProjectId === p.id && <Loader2 size={9} className="animate-spin" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate">{p.project_name}</div>
                  <div className="text-[10.5px] text-[hsl(var(--foreground-muted))] truncate">{p.client}</div>
                </div>
              </button>
            ))}
            {assignedProjects.length > 0 && (
              <>
                <div className="mt-1 px-2 py-1 text-[10px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))]">
                  Already assigned — click to remove
                </div>
                {assignedProjects.map(p => (
                  <button
                    key={p.id}
                    onClick={() => toggle(p.id, true)}
                    disabled={busyProjectId === p.id}
                    data-testid={`unassign-option-${member.id}-${p.id}`}
                    className="w-full text-left px-2.5 py-1.5 rounded text-[12.5px] hover:bg-[hsl(var(--surface-1))] flex items-center gap-2"
                  >
                    <div className="w-4 h-4 rounded border grid place-items-center flex-none bg-[hsl(var(--primary))] text-white"
                         style={{ borderColor: 'hsl(var(--primary))' }}>
                      {busyProjectId === p.id ? <Loader2 size={9} className="animate-spin" /> : <Check size={9} />}
                    </div>
                    <div className="min-w-0 flex-1 truncate">{p.project_name}</div>
                  </button>
                ))}
              </>
            )}
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
